import { useEffect, useRef, useState } from "react";
import axiosClient from "../../axiosClient";
import { format } from "date-fns";
import classNames from "classnames";
import {
  InformationCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import PreviousSessionsDetailsDrawer from "../PreviousSessionsDetailsDrawer/PreviousSessionsDetailsDrawer";
import { ClipLoader } from "react-spinners";

interface PreviousExerciseSessionsProps {
  currentExercise: Exercise | null;
  onHeightChange: (height: number) => void;
}

const PreviousExerciseSessions: React.FC<PreviousExerciseSessionsProps> = ({
  currentExercise,
  onHeightChange,
}) => {
  const [allSessionsOfExercise, setAllSessionsOfExercise] = useState<
    ExerciseWithParentWorkout[] | null
  >(null);
  const [isWorkoutDetailsDrawerOpen, setIsWorkoutDetailsDrawerOpen] =
    useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const containerRef = useRef<HTMLDivElement>(null);
  const prevExerciseNameRef = useRef<string | undefined>();

  const [currentIndex, setCurrentIndex] = useState(0);

  const handleNext = () => {
    if (!allSessionsOfExercise) return;
    setCurrentIndex((prevIndex) =>
      prevIndex < allSessionsOfExercise.length - 1 ? prevIndex + 1 : prevIndex
    );
  };

  const handlePrevious = () => {
    setCurrentIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
  };

  useEffect(() => {
    if (containerRef.current) {
      const observer = new ResizeObserver(() => {
        const heightWithPaddingAndBorder =
          containerRef.current?.getBoundingClientRect().height || 0;
        onHeightChange(heightWithPaddingAndBorder);
      });

      observer.observe(containerRef.current);

      return () => observer.disconnect();
    }
  }, [onHeightChange]);

  useEffect(() => {
    const currentName = currentExercise?.name;
    const workoutId = currentExercise?.workoutId;

    if (
      currentName &&
      currentName !== prevExerciseNameRef.current &&
      workoutId
    ) {
      const fetchSessions = async () => {
        setAllSessionsOfExercise(null);
        setCurrentIndex(0);
        setIsLoading(true);
        try {
          const fetchPreviousSessionsResponse = await axiosClient.get(
            `/api/exercises/previousSessionsOfExercise`,
            {
              params: {
                exerciseName: currentName,
                includeWorkouts: true,
                workoutsToExclude: [workoutId],
                newestFirst: true,
                populateWorkoutExercises: true,
              },
            }
          );
          const previousSessions = fetchPreviousSessionsResponse.data;
          setAllSessionsOfExercise(previousSessions);
        } catch (e) {
          console.error("Error fetching sessions of exercise: " + e);
        } finally {
          setIsLoading(false);
        }
      };

      fetchSessions();
    } else if (!currentName) {
      setAllSessionsOfExercise(null);
      setCurrentIndex(0);
    }

    prevExerciseNameRef.current = currentName;
  }, [currentExercise?.name, currentExercise?.workoutId]);

  const showLoadingState =
    isLoading || (currentExercise?.name && !allSessionsOfExercise);

  if (currentExercise && currentExercise.name === "") {
    return (
      <div
        ref={containerRef}
        className="p-2 bg-indigo-300 absolute w-full bottom-[63px] h-[88px]"
      >
        <p className="text-gray-900 text-sm font-medium mb-2">
          Exercise History
        </p>
        <p className="text-sm text-gray-900 mb-1">
          Please enter an <span className="text-indigo-600">exercise name</span>{" "}
          to see any previous sessions of the exercise.
        </p>
      </div>
    );
  }

  if (showLoadingState) {
    return (
      <div
        ref={containerRef}
        className="p-2 bg-indigo-300 absolute w-full bottom-[63px]"
      >
        <p className="text-gray-900 text-sm font-medium">Exercise History</p>
        <div className="flex flex-col items-center w-full p-[10px]">
          <ClipLoader color="#4f46e5" speedMultiplier={0.6} size={"2rem"} />
        </div>
      </div>
    );
  }

  if (!allSessionsOfExercise || allSessionsOfExercise.length === 0) {
    return (
      <div
        ref={containerRef}
        className="p-2 bg-indigo-300 absolute w-full bottom-[63px]"
      >
        <p className="text-gray-900 text-sm font-medium mb-2">
          No Exercise History Found!
        </p>
        <p className="text-sm text-gray-900 mb-1">
          Once you complete your first{" "}
          <span className="text-indigo-600">
            {currentExercise?.name || "Untitled Exercise"}
          </span>{" "}
          session, you'll see your progress here.
        </p>
      </div>
    );
  }

  const currentSessionData = allSessionsOfExercise[currentIndex];

  return (
    <div
      ref={containerRef}
      className="p-2 bg-indigo-300 absolute w-full bottom-[63px]"
    >
      <div className="flex flex-row justify-between mb-2">
        <p className="text-gray-900 text-sm font-medium">Exercise History</p>
        <div className="grid grid-cols-[auto,110px,auto] items-center">
          <button
            onClick={handleNext}
            disabled={currentIndex === allSessionsOfExercise.length - 1}
          >
            <ChevronLeftIcon
              className={classNames("h-4 w-4 text-gray-900", {
                "opacity-40": currentIndex === allSessionsOfExercise.length - 1,
              })}
              aria-hidden="true"
            />
          </button>
          <span className="text-sm text-gray-900 font-medium w-full text-center text-ellipsis overflow-hidden whitespace-nowrap block">
            {format(new Date(currentSessionData.date), "iii MMM d, yy")}
          </span>
          <button onClick={handlePrevious} disabled={currentIndex === 0}>
            <ChevronRightIcon
              className={classNames("h-4 w-4 text-gray-900", {
                "opacity-40": currentIndex === 0,
              })}
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      <div key={currentSessionData._id} className="flex flex-col">
        <button
          className="flex items-center gap-2"
          onClick={() => {
            setIsWorkoutDetailsDrawerOpen(true);
          }}
        >
          <span className="overflow-hidden text-ellipsis whitespace-nowrap max-w-full text-indigo-600 font-bold text-sm">
            {currentSessionData.parentWorkout.title || "Untitled Workout"}
          </span>

          <InformationCircleIcon
            className="h-5 w-5 shrink-0 text-indigo-600"
            aria-hidden="true"
          />
        </button>

        <div className="mt-1 text-sm text-gray-900 text-ellipsis overflow-hidden w-full whitespace-nowrap flex flex-row gap-x-3 gap-y-1 flex-wrap">
          {currentSessionData.sets.length > 0 ? (
            currentSessionData.sets.map((set: ExerciseSet, index: number) => {
              return (
                <div key={index} className="flex flex-row gap-x-1">
                  <span className="font-medium">
                    Set{`\xa0`}
                    {index + 1}:
                  </span>
                  <span>
                    {!set.weight ? 0 : set.weight}x{!set.reps ? 0 : set.reps}
                  </span>
                </div>
              );
            })
          ) : (
            <div>No sets added</div>
          )}
        </div>
      </div>
      <PreviousSessionsDetailsDrawer
        allSessionsOfExercise={allSessionsOfExercise}
        currentSessionIndex={currentIndex}
        setCurrentSessionIndex={setCurrentIndex}
        isOpen={isWorkoutDetailsDrawerOpen}
        onClose={() => {
          setIsWorkoutDetailsDrawerOpen(false);
        }}
      />
    </div>
  );
};

export default PreviousExerciseSessions;
